import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';
import { usePageTitleAddon } from './PageTitleAddon';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReadonlyArray<BreadcrumbItem>;
  /**
   * Optional back link rendered before the title (e.g. detail → list).
   */
  backHref?: string;
  backLabel?: string;
  /**
   * When true, the header sticks to the top of the scroll container. Default
   * false so existing usages stay unchanged.
   */
  sticky?: boolean;
  /**
   * Dense layout — smaller title (text-lg) and reduced bottom padding.
   * Default true (R1 baseline 카페24 정합). Pass false to preserve the legacy
   * spacious header (26px title + pb-5).
   */
  dense?: boolean;
}

export const PageHeader = ({
  title,
  description,
  actions,
  breadcrumb,
  backHref,
  backLabel,
  sticky = false,
  dense = true,
  className,
  style,
  ...props
}: PageHeaderProps) => {
  const titleAddon = usePageTitleAddon();
  const titleClass = dense
    ? 'text-lg font-semibold tracking-tight text-[color:var(--content-strong)]'
    : 'text-[26px] font-semibold tracking-tight text-[color:var(--content-strong)]';
  const padClass = dense ? 'pb-3' : 'pb-5';

  return (
    <header
      className={cn('flex flex-col gap-2', padClass, className)}
      style={{
        borderBottom: '1px solid var(--divider-strong)',
        ...(sticky
          ? {
              position: 'sticky',
              top: 0,
              zIndex: 20,
              background: 'var(--surface-0)',
              paddingTop: '12px',
            }
          : null),
        ...style,
      }}
      {...props}
    >
      {breadcrumb && breadcrumb.length > 0 ? (
        <nav aria-label="breadcrumb">
          <ol
            className="flex items-center gap-1.5 text-[12px]"
            style={{ color: 'var(--content-muted)' }}
          >
            {breadcrumb.map((item, idx) => (
              <li key={`${item.label}-${idx}`} className="flex items-center gap-1.5">
                {idx > 0 ? <span aria-hidden="true">/</span> : null}
                {item.href ? (
                  <a
                    href={item.href}
                    className="hover:text-[color:var(--content-strong)] hover:underline"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span>{item.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      {backHref ? (
        <a
          href={backHref}
          className="inline-flex w-fit items-center gap-1 text-[12px] hover:underline"
          style={{ color: 'var(--content-muted)' }}
        >
          <span aria-hidden="true">&#8249;</span>
          {backLabel ?? '뒤로'}
        </a>
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className={titleClass} style={{ lineHeight: 'var(--leading-heading)' }}>
              {title}
            </h1>
            {titleAddon}
          </div>
          {description ? (
            <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
              {description}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div
            className="flex shrink-0 items-center gap-2"
            style={sticky ? { position: 'sticky', right: 0 } : undefined}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
};
